import React from 'react';
import { connect } from 'react-redux';
import {
    Card,
    CardContent,
    CardMedia,
    CardActionArea,
    Grid,
    CardActions,
    IconButton,
    Link,
    Typography
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { mediaPickerActions } from '../../../store';
import AspectRatioRoundedIcon from '@mui/icons-material/AspectRatioRounded';
import { createMediaSource, createMediaSourceDataForVideo, createLocalMediaSourceUrls, getVideoUrlForResolution } from '../../../../helpers';
import { MEDIA_RESOURCE_TYPE, MEDIA_SOURCE_TYPE, VIDEO_RESOLUTIONS } from '../../../../CreatePageConstants';
import { createActions } from '../../../../store';
import { AnalyticsConstants, trackEvent } from '../../../../../../../utilities/analyticsUtilities';
import AppConstants from '../../../../../../../App/AppConstants';
import { getVideoElementPlaying } from '../../../../../../../utilities/basicUtilities';
import { isSafariBrowser } from '../../../../../../../utilities/browserUtilities';
import ProMediaCardLabel from '../../../common/ProMediaCardLabel';
import ResolutionMediaCardLabel from '../../../common/ResolutionMediaCardLabel';
import { appOperations } from '../../../../../../../App/store';
import ColoredSnackbarConstants from '../../../../../../misc/ColoredSnackbar/ColoredSnackbarConstants';

const styles = theme => ({
    videoCard: {
        margin: theme.spacing(0.5),
        '&:hover': {
            '& .MuiCardActions-root': {
                display: 'flex'
            },
        },
        overflow: 'hidden',
        cursor: 'pointer'
    },
    videoCardContent: {
        padding: 0,
        height: '100%'
    },
    videoCardActions: {
        display: 'none',
        justifyContent: 'flex-end',
        marginTop: -125,
    },
    footerActions: {
        display: 'none',
        marginTop: '50px',
        padding: 0,
        justifyContent: 'flex-end'
    },
    videoAuthorLabel: {
        bottom: theme.spacing(0),
        background: '#00000061',
        width: '100%',
        padding: theme.spacing(0.5),
    },
    previewButton: {
        padding: 2
    }
});

const BackgroundStockVideosCard = (props) => {
    const { 
        classes, stockVideo, galleryCardWidth, galleryCardHeight, showStockVideoPreview, setMediaPickersProperty, 
        updateBackgroundSettings, videoSettingsId, setPageProperty, subscriptionType, openPremiumVideosDialog,
        savingInProgress, showCriticalOperationProgressWarning, isVideoExportsQueued, openSnack
    } = props;

    const isSafari = isSafariBrowser();
    const isProfessionalStockVideo = subscriptionType === AppConstants.FREE_SUBSCRIPTION_TYPE;

    const p360Video = getVideoUrlForResolution(stockVideo.stockVideoUrls, VIDEO_RESOLUTIONS.p360.height);
    const p540Video = getVideoUrlForResolution(stockVideo.stockVideoUrls, VIDEO_RESOLUTIONS.p540.height);
    const p720Video = getVideoUrlForResolution(stockVideo.stockVideoUrls, VIDEO_RESOLUTIONS.p720.height);
    const p1080Video = getVideoUrlForResolution(stockVideo.stockVideoUrls, VIDEO_RESOLUTIONS.p1080.height);

    const onVideoClick = () => {
        if (savingInProgress) {
            showCriticalOperationProgressWarning();
            return;
        }

        if (isVideoExportsQueued())
            return;

        // Check the stock video id to be not empty and contains numbers only.
        if (!stockVideo.id || !/^[0-9]+$/.test(stockVideo.id)) {
            openSnack("This video is unavailable right now. Please try again later.", ColoredSnackbarConstants.SNACKBAR_WARNING);
            return;
        }
  
        if (isProfessionalStockVideo) {
            setMediaPickersProperty('backgroundMediaPickerOpen', false);
            openPremiumVideosDialog(p360Video.url, () => {
                setMediaPickersProperty('backgroundMediaPickerOpen', true);
            }, 'stock videos');
        } 
        else {
            const mediaData = createMediaSourceDataForVideo(p1080Video.url, p720Video.url, p540Video.url, p360Video.url);
            const mediaSource = createMediaSource(mediaData, MEDIA_SOURCE_TYPE.STOCK_VIDEO, stockVideo.id, MEDIA_RESOURCE_TYPE.VIDEO);
    
            setPageProperty('backgroundVideosLoading', true);
            createLocalMediaSourceUrls(mediaSource,
                (localMediaSource) => {
                    updateBackgroundSettings({ mediaSource: localMediaSource });
                    setPageProperty('backgroundVideosLoading', false);
                }, () => {
                    setPageProperty('backgroundVideosLoading', false);
                }, window.createPageAbortController
            );
    
            setMediaPickersProperty('backgroundMediaPickerOpen', false);

            trackEvent('Assigned Media', {
                category: AnalyticsConstants.VIDEO_CATEGORY,
                type: 'background',
                source: 'Stock Videos',
                videoId: videoSettingsId,
                catalogVideoId: stockVideo.id
            });
        }
    };

    const onPreviewClick = (event) => {
        showStockVideoPreview(stockVideo.id, true, {
            exampleUrl: p540Video?.url,
            p1080Url: p1080Video.url,
            p720Url: p720Video.url,
            p540Url: p540Video.url,
            p360Url: p360Video.url
        });
        event.stopPropagation();
    };

    const onCardMouseEnter = (event) => {
        const videoElement = event.currentTarget.querySelector('video');
        let isPlaying = getVideoElementPlaying(videoElement);

        if (!isPlaying) {
            if (videoElement) {
                videoElement.play().catch(error => {
                    console.error('Failed to play video:', error);
                });
            }
        }
    };

    const onCardMouseLeave = (event) => {
        const videoElement = event.currentTarget.querySelector('video');
        let isPlaying = getVideoElementPlaying(videoElement);
        
        if (isPlaying)
            videoElement.pause();
    };

    return (
        <Grid>
            <Card 
                className={classes.videoCard}
                style={{width: galleryCardWidth, height: galleryCardHeight}}
                onClick={onVideoClick}
                onMouseEnter={isSafari ? null : onCardMouseEnter}
                onMouseLeave={isSafari ? null : onCardMouseLeave}
            >
                <CardContent className={classes.videoCardContent}>
                    {isProfessionalStockVideo && <ProMediaCardLabel align="start" width={208} />}
                    { !isProfessionalStockVideo && <ResolutionMediaCardLabel width={208} resolution={stockVideo.maxResolution} /> }

                    {isSafari 
                    ?
                        <CardMedia
                            component={'img'}
                            crossOrigin="anonymous"
                            src={stockVideo.thumbnail}
                        />
                    :
                        <CardMedia
                            component={'video'}
                            crossOrigin="anonymous"
                            src={stockVideo.exampleUrl}
                            muted
                            loop
                            type='video/mp4'
                            preload='auto'
                        />
                    }
                </CardContent>
                <CardActions className={classes.videoCardActions}>
                    <IconButton
                        className={classes.previewButton}
                        aria-label='open video preview'
                        onClick={onPreviewClick}
                        size="large"
                    >
                        <AspectRatioRoundedIcon />
                    </IconButton>
                </CardActions> 
                <CardActions className={classes.footerActions}>
                    <CardActionArea>
                        <Link
                            href={stockVideo.authorUrl}
                            target='_blank'
                            color='textSecondary'
                        >
                            <Typography
                                variant='subtitle2'
                                color='textSecondary'
                                className={classes.videoAuthorLabel}
                            >
                                {stockVideo.title}
                            </Typography>
                        </Link>
                    </CardActionArea>
                </CardActions>
            </Card>
        </Grid>
    );
}

const mapStateToProps = (state, ownProps) => {
    const stockVideo = state.mediaPicker.backgroundStockVideos[ownProps.index];
    const { id: videoSettingsId } = state.createPreview.present.videoSettings;
    const { subscriptionType } = state.app.accountDetails;
    const { savingInProgress } = state.create.videoSavingState;

    const { backgroundStockVideosOrientation } = state.mediaPicker;

    return { 
        stockVideo, 
        videoSettingsId,
        subscriptionType,
        savingInProgress,
        backgroundStockVideosOrientation
    };
}

const mapDispatchToProps = (dispatch) => {
    const isVideoExportsQueued = () => dispatch(createActions.isVideoExportsQueued());
    const setMediaPickersProperty = (key, value) => dispatch(mediaPickerActions.setProperty(key, value));
    const setPageProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const showStockVideoPreview = (stockVideoId, isPremium, urls ) => dispatch(mediaPickerActions.showStockVideoPreview(stockVideoId, isPremium, urls));
    const updateBackgroundSettings = (properties) => dispatch(createActions.updateBackgroundSettings(properties));
    const openPremiumVideosDialog = (videoUrl, onGoBack, location) => dispatch(mediaPickerActions.openPremiumVideosDialog(videoUrl, onGoBack, location));
    const showCriticalOperationProgressWarning = () => dispatch(createActions.showCriticalOperationProgressWarning());
    const openSnack = (text, variant) => dispatch(appOperations.openSnack(text, variant));

    return { 
        setMediaPickersProperty,
        setPageProperty,
        showStockVideoPreview,
        updateBackgroundSettings,
        openPremiumVideosDialog,
        isVideoExportsQueued,
        showCriticalOperationProgressWarning,
        openSnack
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(BackgroundStockVideosCard, styles));